/* ============================================================
   OFFERTE-PDF — GEDEELDE PDF-MOTOR
   Hoort in: assets/offerte-pdf.js

   Dit bestand bevat de PDF-opmaakmotor voor de dealer-offertes, GEDEELD
   door zowel de hoofdconfigurator (configurator.js) als de REBUILD-tool
   (rebuild-configurator.js). Eerder stond deze code alleen in
   configurator.js; op 7 sep 2026 hierheen verplaatst zodat de rebuild-tool
   dezelfde (grondig geteste) PDF-motor kan hergebruiken in plaats van
   ~350 regels font-inbak-/foto-terugval-/lay-outlogica te moeten kopiëren.

   BELANGRIJK -- laadvolgorde: dit bestand moet in de Liquid-sectie NA
   jspdf.umd.min.js en NA pdf-lettertype.js komen, maar VOOR configurator.js
   / rebuild-configurator.js zelf. Alleen laden in dealer-modus, want de
   gewone webshop-pagina's gebruiken dit niet.

   WAT DIT BESTAND WEL DOET: de PDF zelf opbouwen (logo, tekst, foto's,
   paginering, lettertype). WAT DIT BESTAND NIET DOET: weten welke
   artikelen gekozen zijn, wat de knoppen doen, of hoe de PDF daarna
   gedownload/gemaild wordt -- dat blijft per tool in configurator.js resp.
   rebuild-configurator.js staan (bouwOfferteRegels/rBouwOfferteRegels,
   downloadOffertePdf, openOfferteMailConcept, enz.), want dat is precies
   het deel dat per tool verschilt.

   AANROEP (het enige dat een tool nodig heeft):
     const pdfBytes = await window.OffertePdf.maakOffertePdfV2({
       regels, metPrijzen, totaalTekst,
       klantNaam, klantEmail, dealerNaam, dealerEmail, bedTitel, datumTekst,
       logoUrl,
       vertaal: t,   // VERPLICHT: de eigen vertaalfunctie van de aanroepende
                     // tool, bv. t(key, vars) of rt(key, vars) -- moet
                     // dezelfde sleutels kennen als hieronder gebruikt
                     // worden (quotePdfTitle, quotePdfDealer, quotePdfDate,
                     // quotePdfCustomer, quotePdfSku, quotePdfQty,
                     // quotePdfTotal, quotePdfNoPrices, quotePdfFooter,
                     // quotePdfContinued). Zo blijft deze motor volledig los
                     // van de vertaaltabel/naamgeving van een specifieke tool.
     });
   `regels` is een array van { titel, sku, aantal, prijsTekst, totaalTekst,
   fotoUrl, fotoUrlTerugval } -- fotoUrlTerugval is optioneel en wordt
   geprobeerd als fotoUrl een 404 geeft (zie laadAfbeeldingAlsDataUrl/
   maakOffertePdfV2 hieronder).

   Tekst: jsPDF's ingebouwde Helvetica gebruikt zelf al WinAnsiEncoding,
   dus å/æ/ø (Deens), ä/ö/ü/ß (Duits), é/è/ç (Frans) en het euroteken €
   werken zonder een eigen lettertype te hoeven meeleveren. Een teken
   daarbuiten (bv. Chinese tekens) wordt via pdfVeiligeTekst() vervangen
   door "?", zodat de PDF nooit onleesbare/verschoven tekst bevat. Is
   pdf-lettertype.js WEL geladen (zelf-gehost Noto Sans), dan wordt dat
   ingebakken lettertype gebruikt in plaats van Helvetica -- dat lost een
   inconsistentie op die in Adobe Reader is waargenomen (verkeerd euroteken
   bij het normale gewicht, wel goed bij het vette gewicht). Ontbreekt
   pdf-lettertype.js, dan valt dit automatisch en onopgemerkt terug op
   Helvetica; er gaat niets stuk.

   Afbeeldingen (logo + productfoto's) worden hier opgehaald via fetch() en
   als data-URL ingeladen; lukt dat niet (geen netwerk, verwijderde foto,
   CORS-blokkade, HTTP 404 omdat het bestand nooit in Shopify's Files is
   geupload), dan wordt -- als er een fotoUrlTerugval is meegegeven --
   die geprobeerd; lukt ook dat niet, dan slaat de PDF gewoon die ene
   afbeelding over. De offerte mag daar nooit volledig op vastlopen.
   ============================================================ */
window.OffertePdf = (function () {

  // Unicode-codepoints die we op de PDF kunnen tonen, boven op de gewone
  // Latin-1-reeks (0-255): het euroteken, "slimme" aanhalingstekens/
  // gedachtestreepjes uit bijv. Word/Shopify-teksten, enzovoort. Dit is puur
  // om te herkennen wát we kunnen tonen -- de daadwerkelijke omzetting doet
  // jsPDF zelf (bij het oude, niet-ingebakken "Helvetica") resp. het eigen
  // ingebakken lettertype (pdf-lettertype.js, zie pdfRegistreerLettertype).
  // Twee zeldzame tekens -- ‡ (dubbele dolk, 0x2021) en ‰ (promille, 0x2030)
  // -- zitten NIET in de subset van dat ingebakken lettertype (Google Fonts'
  // eigen "latin"/"latin-ext"-indeling laat ze weg) en zouden anders
  // stilzwijgend verdwijnen in plaats van netjes "?" te worden; vandaar dat
  // ze hier bewust ontbreken. Komt dat in de praktijk voor (zeer
  // onwaarschijnlijk bij bed-/accessoirenamen/onderdeelnamen), dan toont de
  // PDF een "?" in plaats van het teken domweg weg te laten.
  const PDF_WINANSI_EXTRA_CODEPOINTS = new Set([
    0x20AC, 0x201A, 0x0192, 0x201E, 0x2026, 0x2020, 0x02C6,
    0x0160, 0x2039, 0x0152, 0x017D, 0x2018, 0x2019, 0x201C, 0x201D, 0x2022,
    0x2013, 0x2014, 0x02DC, 0x2122, 0x0161, 0x203A, 0x0153, 0x017E, 0x0178,
  ]);
  function pdfVeiligeTekst(str){
    let out = "";
    for (const ch of String(str == null ? "" : str)) {
      const code = ch.codePointAt(0);
      out += (code <= 255 || PDF_WINANSI_EXTRA_CODEPOINTS.has(code)) ? ch : "?";
    }
    return out;
  }

  // Zet een ArrayBuffer om naar base64 -- werkt zowel in de browser (btoa) als
  // in de Node-testomgeving (Buffer), zodat dezelfde code overal draait.
  function pdfArrayBufferNaarBase64(buf){
    const bytes = new Uint8Array(buf);
    let binair = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binair += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    if (typeof btoa === "function") return btoa(binair);
    if (typeof Buffer !== "undefined") return Buffer.from(bytes).toString("base64");
    return "";
  }

  // Voegt (of vervangt) de width-parameter aan een Shopify CDN-afbeeldings-URL
  // toe, zodat we een verkleind bestand ophalen i.p.v. de volle foto. Forceert
  // bewust GEEN "format=jpg" (jsPDF herkent WEBP/PNG/JPEG allemaal correct aan
  // de data zelf, zie laadAfbeeldingAlsDataUrl). Lukt het URL-parsen niet
  // (geen geldige/absolute URL), dan geven we de oorspronkelijke URL terug;
  // laadAfbeeldingAlsDataUrl() vangt een eventuele vervolgfout daarna op.
  function pdfAfbeeldingUrl(url, maxBreedtePx){
    if (!url) return "";
    try {
      let abs = url;
      if (abs.startsWith("//")) abs = "https:" + abs;
      const basis = (typeof window !== "undefined" && window.location) ? window.location.href : undefined;
      const u = new URL(abs, basis);
      u.searchParams.set("width", String(maxBreedtePx));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  // Zet een MIME-type ("image/webp" e.d.) om naar de PDF-formaatnaam die
  // jsPDF gebruikt. Puur informatief: jsPDF's addImage() herkent het echte
  // bestandsformaat zelf aan de data-URL, dus een verkeerde gok hier leidt
  // niet tot een kapotte PDF.
  function pdfFormatVanMime(mime){
    const m = (mime || "").toLowerCase();
    if (m.includes("png")) return "PNG";
    if (m.includes("webp")) return "WEBP";
    if (m.includes("gif")) return "GIF";
    if (m.includes("bmp")) return "BMP";
    return "JPEG";
  }

  // Haalt een afbeelding op en zet die om naar een data-URL + PDF-formaat.
  // Geeft null terug bij elke soort mislukking (geen URL, geen fetch
  // beschikbaar, netwerkfout, geen 200 OK) -- nooit een fout die de hele
  // offerte laat mislukken; de aanroeper laat de afbeelding dan gewoon weg.
  // Bij een mislukking komt er wel een console.warn("[offerte-PDF] ...") met
  // de URL en de reden -- controleer die (F12 → Console) als een foto
  // onverwacht ontbreekt op de PDF.
  async function laadAfbeeldingAlsDataUrl(url){
    if (!url) return null;
    if (typeof fetch !== "function") {
      console.warn("[offerte-PDF] geen fetch() beschikbaar, afbeelding overgeslagen:", url);
      return null;
    }
    try {
      const res = await fetch(url, { mode: "cors" });
      if (!res || !res.ok) {
        console.warn(`[offerte-PDF] afbeelding niet opgehaald (status ${res ? res.status : "?"}):`, url);
        return null;
      }
      const blob = await res.blob();
      const buf = await blob.arrayBuffer();
      const base64 = pdfArrayBufferNaarBase64(buf);
      if (!base64) {
        console.warn("[offerte-PDF] afbeelding opgehaald maar kon niet naar base64 omgezet worden:", url);
        return null;
      }
      const type = (blob.type || "").toLowerCase();
      return { dataUrl: `data:${type || "image/jpeg"};base64,${base64}`, format: pdfFormatVanMime(type) };
    } catch (e) {
      console.warn("[offerte-PDF] afbeelding laden mislukt:", url, e && e.message);
      return null;
    }
  }

  // Schaalt (breedte,hoogte) in pixels naar een bounding box in punten, met
  // behoud van beeldverhouding -- zodat een logo of productfoto nooit
  // vervormd op de PDF komt.
  function pdfPasIn(breedte, hoogte, maxBreedte, maxHoogte){
    if (!breedte || !hoogte) return { w: maxBreedte, h: maxHoogte };
    const schaal = Math.min(maxBreedte / breedte, maxHoogte / hoogte);
    return { w: breedte * schaal, h: hoogte * schaal };
  }

  // Tekent een afbeelding binnen een bounding box (linksboven op x,y),
  // gecentreerd. Volledig beschermd met try/catch: een corrupte/onverwachte
  // data-URL mag nooit de rest van de PDF laten mislukken -- die regel/dat
  // logo wordt dan gewoon zonder afbeelding getekend.
  function pdfTekenAfbeelding(doc, imgData, x, y, maxW, maxH){
    if (!imgData) return;
    try {
      const props = doc.getImageProperties(imgData.dataUrl);
      const box = pdfPasIn(props.width, props.height, maxW, maxH);
      const ix = x + (maxW - box.w) / 2;
      const iy = y + (maxH - box.h) / 2;
      doc.addImage(imgData.dataUrl, imgData.format, ix, iy, box.w, box.h);
    } catch (e) {
      // Afbeelding niet leesbaar door jsPDF -- gewoon overslaan, met een
      // console.warn zodat dit terug te vinden is (zie laadAfbeeldingAlsDataUrl).
      console.warn("[offerte-PDF] afbeelding kon niet in de PDF gezet worden:", e && e.message);
    }
  }

  // Bakt het zelf-gehoste Noto Sans-lettertype (pdf-lettertype.js) in de PDF
  // in, zodat het euroteken en andere tekens niet meer afhangen van wat de
  // PDF-lezer zelf invult voor jsPDF's niet-ingebakken "Helvetica" (zie
  // pdf-lettertype.js voor de volledige uitleg: in Adobe Reader bleek het
  // euroteken bv. wel te tonen in het vette gewicht, maar niet in het
  // normale). Geeft de te gebruiken font-naam terug: "LifetimeSans" als het
  // inbakken lukt, anders "helvetica" als nette terugval (bv. als
  // pdf-lettertype.js nog niet is geupload) -- de PDF blijft dan werken
  // zoals voorheen.
  function pdfRegistreerLettertype(doc){
    const w = (typeof window !== "undefined") ? window : (typeof globalThis !== "undefined" ? globalThis : {});
    const regular = w.PDF_LETTERTYPE_REGULAR_BASE64;
    const bold = w.PDF_LETTERTYPE_BOLD_BASE64;
    if (!regular || !bold) return "helvetica";
    try {
      doc.addFileToVFS("NotoSans-Regular.ttf", regular);
      doc.addFont("NotoSans-Regular.ttf", "LifetimeSans", "normal");
      doc.addFileToVFS("NotoSans-Bold.ttf", bold);
      doc.addFont("NotoSans-Bold.ttf", "LifetimeSans", "bold");
      return "LifetimeSans";
    } catch (e) {
      console.warn("[offerte-PDF] eigen lettertype inbakken mislukt, terugval op Helvetica:", e && e.message);
      return "helvetica";
    }
  }

  // Kort tekst af tot hij past binnen maxWidthPt, op basis van jsPDF's eigen
  // lettertypemetriek (getTextWidth) -- preciezer dan een vuistregel, omdat
  // dit de daadwerkelijk ingestelde font/grootte van `doc` gebruikt.
  function pdfKortAf(doc, tekst, maxWidthPt){
    const veilig = pdfVeiligeTekst(tekst);
    if (doc.getTextWidth(veilig) <= maxWidthPt) return veilig;
    let lo = 0, hi = veilig.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const kandidaat = veilig.slice(0, mid) + "…";
      if (doc.getTextWidth(kandidaat) <= maxWidthPt) lo = mid; else hi = mid - 1;
    }
    return veilig.slice(0, lo) + "…";
  }

  // Het enige dat een tool hoeft aan te roepen -- zie de uitleg bovenaan dit
  // bestand voor de volledige parameterlijst. `vertaal` is verplicht: de
  // eigen t()/rt()-functie van de aanroepende tool.
  async function maakOffertePdfV2({ regels, metPrijzen, totaalTekst, klantNaam, klantEmail, dealerNaam, dealerEmail, bedTitel, datumTekst, logoUrl, vertaal }){
    if (typeof vertaal !== "function") {
      throw new Error("maakOffertePdfV2: 'vertaal' (de eigen t()/rt()-functie van de tool) is verplicht.");
    }
    const t = vertaal;
    const jspdfNs = (typeof window !== "undefined" && window.jspdf) || (typeof jspdf !== "undefined" ? jspdf : null);
    if (!jspdfNs || !jspdfNs.jsPDF) {
      throw new Error("jsPDF is niet geladen (jspdf.umd.min.js ontbreekt of is niet ingeladen).");
    }
    const { jsPDF } = jspdfNs;

    // Logo en productfoto's alvast parallel ophalen -- ontbreekt fetch (geen
    // browser) of lukt het niet, dan blijft dat gewoon null en tekent de PDF
    // die afbeelding simpelweg niet. Lukt de eerste (speciale
    // "configurator_SKU"-)foto niet -- bijvoorbeeld omdat dat bestand nooit in
    // Shopify's Files is geupload, wat een gewone HTTP 404 geeft, geen
    // CORS-fout -- dan proberen we net als het scherm zelf de gewone
    // product-/variantfoto als terugval (fotoUrlTerugval), vóór we de
    // afbeelding pas echt overslaan.
    const [logoData, regelsMetFoto] = await Promise.all([
      laadAfbeeldingAlsDataUrl(pdfAfbeeldingUrl(logoUrl, 300)),
      Promise.all(regels.map(async r => {
        let imgData = await laadAfbeeldingAlsDataUrl(pdfAfbeeldingUrl(r.fotoUrl, 120));
        if (!imgData && r.fotoUrlTerugval) {
          imgData = await laadAfbeeldingAlsDataUrl(pdfAfbeeldingUrl(r.fotoUrlTerugval, 120));
        }
        return { ...r, imgData };
      })),
    ]);

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const FONT = pdfRegistreerLettertype(doc);
    const PAGE_W = doc.internal.pageSize.getWidth();
    const PAGE_H = doc.internal.pageSize.getHeight();
    const MARGIN = 50;
    // Voettekst: alle disclaimers vormen nu één doorlopende lopende tekst
    // (geen aparte regel per zin meer) en die tekst staat -- als een echte
    // voettekst -- onderaan IEDERE pagina, niet alleen de laatste. jsPDF's
    // eigen splitTextToSize breekt deze doorlopende tekst per taal
    // automatisch af op de beschikbare breedte, zodat een taal met langere
    // zinnen (bv. NL, DE) vanzelf meer regels krijgt zonder van de pagina
    // af te lopen. De hoogte hiervan wordt hier alvast berekend, zodat
    // ONDERKANT (de laagste y-positie voor gewone inhoud) op elke pagina
    // rekening houdt met deze vaste voettekst.
    doc.setFont(FONT, "normal");
    doc.setFontSize(8);
    const FOOTER_BREEDTE = PAGE_W - MARGIN * 2;
    const FOOTER_KEYS = ["quotePdfFooter","quotePdfFooterPrices","quotePdfFooterErrors","quotePdfFooterChanges","quotePdfFooterColours","quotePdfFooterAvailability"];
    const FOOTER_TEKST = FOOTER_KEYS
      .map(k => pdfVeiligeTekst(t(k)))
      .filter(Boolean)
      // Niet elke vertaalde zin eindigt op een leesteken -- voeg een punt
      // toe waar die ontbreekt, zodat het samenvoegen tot één lopende zin
      // netjes leesbaar blijft (i.p.v. zinnen die zonder punt aan elkaar
      // plakken).
      .map(zin => /[.!?]$/.test(zin) ? zin : zin + ".")
      .join("  ");
    const FOOTER_REGELHOOGTE = 10;
    const FOOTER_REGELS = FOOTER_TEKST ? doc.splitTextToSize(FOOTER_TEKST, FOOTER_BREEDTE) : [];
    const FOOTER_HOOGTE = FOOTER_REGELS.length * FOOTER_REGELHOOGTE;
    const FOOTER_ONDERRAND = 40;                              // baseline laatste voettekstregel t.o.v. onderkant pagina
    const FOOTER_LAATSTE_BASELINE = PAGE_H - FOOTER_ONDERRAND;
    const FOOTER_EERSTE_BASELINE = FOOTER_LAATSTE_BASELINE - (FOOTER_REGELS.length - 1) * FOOTER_REGELHOOGTE;
    const FOOTER_LIJN_Y = FOOTER_EERSTE_BASELINE - 12;        // dunne scheidingslijn net boven de voettekst
    const ONDERKANT = FOOTER_LIJN_Y - 14;                     // ruimte voor voettekst + scheidingslijn, op elke pagina
    const RIJHOOGTE = 60;
    const FOTO_BOX = 44;
    let y = MARGIN;

    function nieuwePagina(){
      doc.addPage();
      y = MARGIN;
      doc.setTextColor(43, 38, 32);
      doc.setFont(FONT, "bold");
      doc.setFontSize(12);
      doc.text(pdfVeiligeTekst(`${t("quotePdfTitle")} — ${t("quotePdfContinued")}`), MARGIN, y);
      y += 26;
    }

    // --- Kop: logo gecentreerd bovenaan, offertetitel linksboven de
    // klantgegevens (geen bedrijfsnaam als losse tekst -- die staat al in
    // het logo zelf) ---
    const LOGO_MAX_W = 180, LOGO_MAX_H = 60;
    pdfTekenAfbeelding(doc, logoData, (PAGE_W - LOGO_MAX_W) / 2, y, LOGO_MAX_W, LOGO_MAX_H);
    y += LOGO_MAX_H + 22;

    doc.setTextColor(43, 38, 32);
    doc.setFont(FONT, "bold");
    doc.setFontSize(19);
    doc.text(pdfVeiligeTekst(t("quotePdfTitle")), MARGIN, y);
    y += 28;

    // --- Meta: datum, dealer (+e-mail), witregel, klant (+e-mail), witregel,
    // titel van de offerte -- op verzoek gegroepeerd per partij (eerst de
    // dealer met diens eigen e-mailadres, dan de klant), met een lege regel
    // ertussen zodat de twee groepen visueel uit elkaar staan. ---
    doc.setFont(FONT, "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(110, 98, 87);
    doc.text(pdfVeiligeTekst(`${t("quotePdfDate")}: ${datumTekst || ""}`), MARGIN, y);
    y += 14;
    if (dealerNaam) { doc.text(pdfVeiligeTekst(`${t("quotePdfDealer")}: ${dealerNaam}`), MARGIN, y); y += 14; }
    if (dealerEmail) { doc.text(pdfVeiligeTekst(dealerEmail), MARGIN, y); y += 14; }
    y += 14;   // witregel tussen dealer- en klantgegevens
    if (klantNaam) { doc.text(pdfVeiligeTekst(`${t("quotePdfCustomer")}: ${klantNaam}`), MARGIN, y); y += 14; }
    if (klantEmail) { doc.text(pdfVeiligeTekst(klantEmail), MARGIN, y); y += 14; }
    y += 14;   // witregel voor de titel van de offerte
    if (bedTitel) { doc.text(pdfVeiligeTekst(bedTitel), MARGIN, y); y += 14; }
    y += 12;

    doc.setDrawColor(89, 66, 47);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 20;

    // --- Lijst met gekozen artikelen ---
    const textX = MARGIN + FOTO_BOX + 14;
    const rightX = PAGE_W - MARGIN;
    const titelMaxWidth = rightX - textX - (metPrijzen ? 100 : 0);

    regelsMetFoto.forEach(regel => {
      if (y + RIJHOOGTE > ONDERKANT) nieuwePagina();
      const rijBoven = y;

      pdfTekenAfbeelding(doc, regel.imgData, MARGIN, rijBoven, FOTO_BOX, FOTO_BOX);

      doc.setTextColor(43, 38, 32);
      doc.setFont(FONT, "bold");
      doc.setFontSize(10.5);
      doc.text(pdfKortAf(doc, regel.titel, titelMaxWidth), textX, rijBoven + 16);

      doc.setFont(FONT, "normal");
      doc.setFontSize(9);
      doc.setTextColor(110, 98, 87);
      doc.text(
        pdfVeiligeTekst(`${t("quotePdfSku")}: ${regel.sku}    ${t("quotePdfQty")}: ${regel.aantal}`),
        textX, rijBoven + 32
      );

      if (metPrijzen) {
        doc.setTextColor(43, 38, 32);
        doc.setFont(FONT, "normal");
        doc.setFontSize(9.5);
        doc.text(pdfVeiligeTekst(regel.prijsTekst), rightX, rijBoven + 16, { align: "right" });
        doc.setFont(FONT, "bold");
        doc.text(pdfVeiligeTekst(regel.totaalTekst), rightX, rijBoven + 32, { align: "right" });
      }

      doc.setDrawColor(221, 213, 201);
      doc.line(MARGIN, rijBoven + RIJHOOGTE - 10, rightX, rijBoven + RIJHOOGTE - 10);
      y = rijBoven + RIJHOOGTE;
    });

    // --- Totaal ---
    if (y + 40 > ONDERKANT) nieuwePagina();
    y += 6;
    if (metPrijzen && totaalTekst) {
      doc.setTextColor(43, 38, 32);
      doc.setFont(FONT, "bold");
      doc.setFontSize(12);
      doc.text(pdfVeiligeTekst(t("quotePdfTotal")), MARGIN, y);
      doc.text(pdfVeiligeTekst(totaalTekst), rightX, y, { align: "right" });
      y += 24;
    } else if (!metPrijzen) {
      doc.setFont(FONT, "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(110, 98, 87);
      doc.text(pdfVeiligeTekst(t("quotePdfNoPrices")), MARGIN, y);
      y += 20;
    }

    // --- Voettekst + paginanummer: op ELKE pagina herhaald, vast onderaan ---
    // (niet meebewegend met de inhoud), zodat de disclaimers ook zichtbaar
    // zijn op pagina's die alleen productregels bevatten. Het paginanummer
    // wordt altijd getoond zodra er meer dan 1 pagina is; bij 1 pagina is
    // dat niet zinvol en blijft het weg.
    const totaalPaginas = doc.getNumberOfPages();
    for (let p = 1; p <= totaalPaginas; p++) {
      doc.setPage(p);

      if (FOOTER_REGELS.length) {
        doc.setDrawColor(221, 213, 201);
        doc.line(MARGIN, FOOTER_LIJN_Y, PAGE_W - MARGIN, FOOTER_LIJN_Y);

        doc.setFont(FONT, "normal");
        doc.setFontSize(8);
        doc.setTextColor(140, 129, 114);
        FOOTER_REGELS.forEach((regel, i) => {
          doc.text(regel, MARGIN, FOOTER_EERSTE_BASELINE + i * FOOTER_REGELHOOGTE);
        });
      }

      if (totaalPaginas > 1) {
        doc.setFont(FONT, "normal");
        doc.setFontSize(8);
        doc.setTextColor(160, 152, 141);
        doc.text(`${p} / ${totaalPaginas}`, PAGE_W - MARGIN, PAGE_H - 24, { align: "right" });
      }
    }

    return new Uint8Array(doc.output("arraybuffer"));
  }

  return {
    maakOffertePdfV2,
    // Onderstaande alleen ter beschikking gesteld voor eventueel hergebruik/
    // testen; een tool hoeft normaal alleen maakOffertePdfV2 aan te roepen.
    // pdfArrayBufferNaarBase64 (2 okt 2026) wordt ook door configurator.js/
    // rebuild-configurator.js gebruikt om de kant-en-klare PDF als base64
    // mee te sturen naar de nieuwe Supabase-server-route ("Per e-mail
    // versturen"), zodat die logica niet in twee bestanden hoeft te staan.
    pdfVeiligeTekst,
    pdfAfbeeldingUrl,
    laadAfbeeldingAlsDataUrl,
    pdfArrayBufferNaarBase64,
  };
})();